import React, { useState } from "react";
import { Modal, Button, TextField, Grid, Typography, Switch, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { SessionInfo } from "../../data/SessionData";
import './EditUserSessionModal.css';
import { EventsListItem } from '../../data/eventInfo';
import { SelectChangeEvent } from '@mui/material/Select';
import Editor from "../../utils/RTEditor";

interface EditUserSessionModalProps {
  session: SessionInfo;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedSession: SessionInfo) => void;
  events: EventsListItem[];
}

const EditUserSessionModal: React.FC<EditUserSessionModalProps> = ({
  session,
  isOpen,
  onClose,
  onSave,
  events,
}) => {
  const [updatedSession, setUpdatedSession] = useState<SessionInfo>(() => {
    if (!session.Id) {
      // If session doesn't have an Id, it's a new session, so initialize IsWorkshop to false
      return {
        ...session,
        IsWorkshop: false,
      };
    }
    // Otherwise, return the session as it is
    return { ...session };
  });

  const [selectedEventId, setSelectedEventId] = useState<string | undefined>(
    session.EventId
  );
  const selectedEvent = events.find((event) => event.Id === selectedEventId);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSessionChanged, setIsSessionChanged] = useState<boolean>(false);
  const [description, setDescription] = useState(updatedSession.Description || "");

  const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUpdatedSession(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleEditorSave = (editorContent: string) => {
    setDescription(editorContent);
    setUpdatedSession(prevState => ({
      ...prevState,
      Description: description
    }));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setUpdatedSession(prevState => ({
      ...prevState,
      [name]: checked
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setSelectedEventId(value);
    setUpdatedSession(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSave = () => {
    if (!selectedEventId) {
      setValidationError("Please select an event.");
    } else if (!updatedSession.Description || !updatedSession.Title) {
      setValidationError("Description and Title cannot be empty.");
    } else {
      onSave(updatedSession);
      onClose();
      setValidationError(null); // Reset validation error when saving succeeds
    }
  };

  // Check if session has changed whenever updatedSession changes
  React.useEffect(() => {
    setIsSessionChanged(
      JSON.stringify(updatedSession) !== JSON.stringify(session)
    );
  }, [updatedSession, session]);

  return (
    <Modal open={isOpen} onClose={onClose} aria-labelledby="edit-session-modal" aria-describedby="modal-to-edit-session" className="edit-session-modal">
      <div className="edit-session-modal-content">
        <form>
          <Grid container justifyContent="center" alignItems="center" spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5" component="h2" align="center">
                {session.Id ? "Edit Session" : "Create Session"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="event-select-label">Select Event</InputLabel>
                <Select
                  labelId="event-select-label"
                  value={selectedEventId || ''}
                  onChange={handleSelectChange}
                  label="Select Event"
                  name="EventId"
                >
                  {events.map(event => (
                    <MenuItem key={event.Id} value={event.Id}>{event.Name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth name="Title" label="Title" value={updatedSession.Title} onChange={handleTextFieldChange} />
            </Grid>
            <Grid item xs={12}>
              <div className="EditorContainer">
                <Typography variant="h6" component="h2" textAlign="center">
                  Description
                </Typography>
                <Editor
                  editorName="editor"
                  content={updatedSession.Description}
                  saveContent={handleEditorSave}
                  showSaveButton={false}
                />
              </div>
            </Grid>
            <Grid item xs={12} className="workshop-text" justifyContent="center">
              <Typography variant="h6" component="h2" align="center">
                Would the Session be a Workshop?
              </Typography>
            </Grid>
            <Grid item xs={12} container justifyContent="center" alignItems="center">
              <Switch
                name="IsWorkshop"
                checked={updatedSession.IsWorkshop}
                onChange={handleSwitchChange}
                disabled={
                  !selectedEvent ||
                  !selectedEvent.IsWorkshopSessionAllowed ||
                  (updatedSession.Id !== undefined &&
                    updatedSession.Id !== null)
                }
                color={updatedSession.IsWorkshop ? "info" : "info"}
              />
            </Grid>
            <Grid item xs={12}>
              {selectedEventId && !selectedEvent?.IsWorkshopSessionAllowed && (
                <Typography variant="body2" color="error" align="center">
                  This Event does not allow Workshops.
                </Typography>
              )}
            </Grid>
            <Grid
              item
              xs={12}
              container
              justifyContent="center"
              alignItems="center"
            >
              <Button onClick={onClose} className="CancelButtonStyle">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                className="ButtonStyle"
                disabled={!isSessionChanged} // Disable Save button if no changes made
              >
                Save
              </Button>
            </Grid>
            {validationError && (
              <Grid item xs={12}>
                <Typography variant="body2" color="error" align="center">
                  {validationError}
                </Typography>
              </Grid>
            )}
          </Grid>
        </form>
      </div>
    </Modal>
  );
};

export default EditUserSessionModal;
